<template>
  <div class="uk-container test-kits">
    <UserHeader />
    <h1>Profile</h1>

   <div v-if="message" class="uk-alert-primary uk-margin">
      <button
        type="button"
        class="uk-alert-close"
        uk-close
        @click="message=''"
      ></button>
      <span>{{ message }}</span>
    </div>

    <div v-if="error" class="uk-alert-danger uk-margin">
      <button
        type="button"
        class="uk-alert-close"
        uk-close
        @click="error=''"
      ></button>
      <span>{{ error }}</span>
    </div>

    <div v-if="isPro" class="ucari-card uk-margin-bottom">
      <div class="ucari-card-title">Company</div>    
      <div class="uk-grid-small" uk-grid>
        <div class="uk-width-1-1@s">
          <label class="uk-form-label">Name</label>
          <div class="uk-form-controls">
            <input
              class="uk-input"
              type="text"
              v-model.trim="companyForm.companyName"
              placeholder="Your company name"
            />
          </div>
        </div>

        <div class="uk-width-1-1@s">
          <label class="uk-form-label">Logo</label>
          <div class="uk-form-controls">
            <div class="uk-flex uk-flex-middle">
              <!-- Preview -->
              <div class="uk-margin-small-right">
                <img
                  v-if="logoPreviewUrl || profile.companyLogoUrl"
                  :src="logoPreviewUrl || profile.companyLogoUrl"
                  alt="Company logo"
                  style="width:64px;height:64px;object-fit:contain;border:1px solid #e5e5e5;border-radius:8px;padding:6px;background:#fff;"
                />
                <div
                  v-else
                  style="width:64px;height:64px;border:1px dashed #bbb;border-radius:8px;display:flex;align-items:center;justify-content:center;"
                  class="uk-text-muted"
                >
                  <span uk-icon="icon: image"></span>
                </div>
              </div>

              <!-- Upload control -->
              <div>
                <input
                  ref="logoInput"
                  type="file"
                  accept="image/*"
                  class="uk-hidden"
                  @change="onLogoSelected"
                />

                <button
                  type="button"
                  class="upload-button"
                  @click="$refs.logoInput.click()"
                >
                  <span uk-icon="icon: upload" class="upload-button-icon"></span>
                  {{ (logoPreviewUrl || profile.companyLogoUrl) ? 'Change Logo' : 'Upload Logo' }}
                </button>

                <div
                  v-if="logoFileName"
                  class="uk-text-small uk-text-muted uk-margin-small-top"
                >
                  {{ logoFileName }}
                </div>

                <div class="uk-text-small uk-text-muted uk-margin-small-top">
                  PNG or JPG recommended. Max ~5MB.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        class="uk-button uk-button-primary uk-margin-top"
        :disabled="savingCompany || !canSubmitCompany"
        @click="updateCompany"
      >
        Save Company
        <span v-if="savingCompany" class="uk-margin-small-left" uk-spinner="ratio: 0.6"></span>
      </button>
    </div>

    <div class="ucari-card uk-margin-bottom">
      <div class="ucari-card-title">Update Email</div>    

      <div class="uk-margin">
        <label class="uk-form-label">Current Email</label>
        <div class="uk-form-controls">
          <input class="uk-input" type="email" :value="profile.email" disabled />
        </div>
      </div>

      <div class="uk-margin">
        <label class="uk-form-label">New Email</label>
        <div class="uk-form-controls">
          <input
            class="uk-input"
            type="email"
            v-model.trim="emailForm.newEmail"
            placeholder="name@example.com"
            :disabled="savingEmail"
            @keyup.enter="requestEmailChange"
            autocomplete="off"
            autocapitalize="none"
            spellcheck="false"
            name="new-email"
          />
        </div>

        <div v-if="pendingEmail" class="uk-text-small uk-text-muted uk-margin-small-top">
          Pending verification for: <strong>{{ pendingEmail }}</strong>
        </div>
      </div>

      <div class="uk-flex uk-flex-middle">
        <button
          class="uk-button uk-button-primary"
          :disabled="savingEmail || !emailForm.newEmail"
          @click="requestEmailChange"
        >
          Send Verification Email
          <span v-if="savingEmail" class="uk-margin-small-left" uk-spinner="ratio: 0.6"></span>
        </button>

        <button
          v-if="pendingEmail"
          class="uk-button uk-button-default uk-margin-small-left"
          :disabled="savingEmail"
          @click="resendEmailChange"
        >
          Resend
        </button>

        <button
          v-if="pendingEmail"
          class="uk-button uk-button-text uk-button-secondary uk-margin-small-left"
          :disabled="savingEmail"
          @click="cancelPendingEmail"
        >
          Cancel
        </button>
      </div>

      <div class="uk-text-small uk-text-muted uk-margin-small-top">
        Your email won’t change until you click the verification link sent to the new address.
      </div>
    </div>

    <div class="ucari-card uk-margin-bottom">
      <div class="ucari-card-title">Update Password</div>    

      <div class="uk-margin">
        <label class="uk-form-label">Current Password</label>
        <div class="uk-form-controls">
          <input class="uk-input" type="password" v-model="pwForm.currentPassword" :disabled="savingPassword" />
        </div>
      </div>

      <div class="uk-margin">
        <label class="uk-form-label">New Password</label>
        <div class="uk-form-controls uk-position-relative">
          <input
            class="uk-input"
            :type="showNewPassword ? 'text' : 'password'"
            v-model="pwForm.newPassword"
            :disabled="savingPassword"
          />

          <a
            class="uk-form-icon uk-form-icon-flip uk-text-small"
            @click="toggleNewPasswordVisibility"
            style="cursor:pointer;"
          >
            {{ showNewPassword ? 'Hide' : 'Show' }}
          </a>
        </div>
      </div>

      <div class="uk-margin">
        <label class="uk-form-label">Confirm New Password</label>
        <div class="uk-form-controls uk-position-relative">
          <input
            class="uk-input"
            :type="showConfirmPassword ? 'text' : 'password'"
            v-model="pwForm.confirmPassword"
            :disabled="savingPassword"
          />

          <a
            class="uk-form-icon uk-form-icon-flip uk-text-small"
            @click="toggleConfirmPasswordVisibility"
            style="cursor:pointer;"
          >
            {{ showConfirmPassword ? 'Hide' : 'Show' }}
          </a>
        </div>
      </div>

      <div class="password-requirements">
        <div><strong>Password must contain:</strong></div>
        <ul>
          <li :class="{ 'valid': passwordHasLowercase }">One lowercase letter</li>
          <li :class="{ 'valid': passwordHasUppercase }">One uppercase letter</li>
          <li :class="{ 'valid': passwordHasNumber }">One number</li>
          <li :class="{ 'valid': passwordHasSpecialChar }">One of these special character: ! @ # $ % ^ & * ( ) - + { } | ?</li>
          <li :class="{ 'valid': passwordHasLength }">At least 8 characters</li>
          <li :class="{ 'valid': passwordMatches }">Passwords match</li>
        </ul>
      </div>

      <button
        class="uk-button uk-button-primary uk-margin-top"
        :disabled="savingPassword || !canSubmitPassword"
        @click="updatePassword"
      >
        Update Password
        <span v-if="savingPassword" class="uk-margin-small-left" uk-spinner="ratio: 0.6"></span>
      </button>
    </div>
  </div>

  <UcariFooter />
</template>

<script>
import axios from "axios";
import UserHeader from "./UserHeader.vue";
import UcariFooter from "./UcariFooter.vue";

export default {
  name: "UserProfile",
  components: { UserHeader, UcariFooter },

  data() {
    return {
      accessToken: "",
      cid: "",
      isPro: false,

      profile: {
        email: "",
        companyName: "",
        companyLogoUrl: ""
      },

      // Company (pro only)
      companyForm: { companyName: "" },
      selectedLogoFile: null,
      logoPreviewUrl: "",
      logoFileName: "",
      savingCompany: false,

      // Email (everyone)
      emailForm: { newEmail: "" },
      pendingEmail: "",
      savingEmail: false,

      // Password (everyone)
      pwForm: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      },
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      savingPassword: false,

      loading: false,
      message: "",
      error: ""
    };
  },
  computed: {
    isFormInvalid() {
      return this.isPasswordInvalid || this.passwordMismatch || !this.newPassword || !this.confirmNewPassword;
    },
    passwordHasLowercase() {
      return /[a-z]/.test(this.pwForm.newPassword || "");
    },
    passwordHasUppercase() {
      return /[A-Z]/.test(this.pwForm.newPassword || "");
    },
    passwordHasNumber() {
      return /\d/.test(this.pwForm.newPassword || "");
    },
    passwordHasSpecialChar() {
      return /[!@#$%^&*()-+{}|?]/.test(this.pwForm.newPassword || "");
    },
    passwordHasLength() {
      return (this.pwForm.newPassword || "").length >= 8;
    },
    passwordMismatch() {
      const np = this.pwForm.newPassword || "";
      const cp = this.pwForm.confirmPassword || "";
      return !!(np && cp && np !== cp);
    },    

    passwordMeetsRules() {
      return (
        this.passwordHasLowercase &&
        this.passwordHasUppercase &&
        this.passwordHasNumber &&
        this.passwordHasSpecialChar &&
        this.passwordHasLength
      );
    },

    passwordMatches() {
      return (
        (this.pwForm.newPassword || "") &&
        (this.pwForm.confirmPassword || "") &&
        this.pwForm.newPassword === this.pwForm.confirmPassword
      );
    },

    canSubmitCompany() {
      if (!this.isPro) return false;

      const name = (this.companyForm.companyName || "").trim();
      const prevName = (this.profile.companyName || "").trim();

      const nameChanged = name !== prevName;
      const logoChanged = !!this.selectedLogoFile;

      return (logoChanged || (nameChanged && name.length > 0));
    },
  },

  created() {
    document.title = "Profile | UCARI";
    this.loadCredentials();
    this.fetchProfile();
  },

  beforeUnmount() {
    if (this.logoPreviewUrl) URL.revokeObjectURL(this.logoPreviewUrl);
  },

  methods: {
    loadCredentials() {
      this.accessToken = localStorage.getItem("accessToken") || "";
      this.cid = localStorage.getItem("cid") || "";
      const isProValue = localStorage.getItem("isPro");
      this.isPro = isProValue === "1" || isProValue === "true";
    },

    apiHeaders() {
      return {
        Authorization: `Bearer ${this.accessToken}`,
        cid: this.cid
      };
    },

    clearAlerts() {
      this.message = "";
      this.error = "";
    },

    async fetchProfile() {
      this.clearAlerts();
      this.loading = true;

      try {
        const resp = await axios.get(`${process.env.VUE_APP_API_URL}/api/profile`, {
          headers: this.apiHeaders()
        });

        this.profile.email = resp.data?.email || "";

        // Only fill these if you’re returning them; harmless if empty
        this.profile.companyName = resp.data?.companyName || "";
        this.profile.companyLogoUrl = resp.data?.companyLogoUrl || "";

        this.companyForm.companyName = this.profile.companyName || "";
      } catch (e) {
        this.error = e.response?.data?.error || "Failed to load profile.";
      } finally {
        this.loading = false;
      }
    },
    onLogoSelected(e) {
      this.clearAlerts();
      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        this.error = "Please select an image file.";
        return;
      }

      const maxBytes = 5 * 1024 * 1024;
      if (file.size > maxBytes) {
        this.error = "Logo is too large. Please upload an image under 5MB.";
        return;
      }

      this.selectedLogoFile = file;
      this.logoFileName = file.name;

      if (this.logoPreviewUrl) URL.revokeObjectURL(this.logoPreviewUrl);
      this.logoPreviewUrl = URL.createObjectURL(file);
    },
    async updateCompany() {
      if (!this.isPro) return;

      this.clearAlerts();
      this.savingCompany = true;

      try {
        const fd = new FormData();
        if (this.companyForm.companyName) fd.append("companyName", this.companyForm.companyName);
        if (this.selectedLogoFile) fd.append("logo", this.selectedLogoFile);

        const resp = await axios.put(`${process.env.VUE_APP_API_URL}/api/profile/company`, fd, {
          headers: {
            ...this.apiHeaders(),
            "Content-Type": "multipart/form-data"
          }
        });

        this.profile.companyName = resp.data?.companyName ?? this.companyForm.companyName ?? "";

        const updatedLogoUrl = resp.data?.companyLogoUrl || "";
        this.profile.companyLogoUrl = updatedLogoUrl
          ? `${updatedLogoUrl}${updatedLogoUrl.includes("?") ? "&" : "?"}v=${Date.now()}`
          : "";

        // reset local selection
        this.selectedLogoFile = null;
        this.logoFileName = "";
        if (this.logoPreviewUrl) URL.revokeObjectURL(this.logoPreviewUrl);
        this.logoPreviewUrl = "";

        this.message = "Company settings saved.";
      } catch (e) {
        this.error = e.response?.data?.error || "Failed to save company settings.";
      } finally {
        this.savingCompany = false;
      }
    },
    async requestEmailChange() {
      this.clearAlerts();

      const newEmail = (this.emailForm.newEmail || "").trim().toLowerCase();
      if (!newEmail) return;

      const re = /\S+@\S+\.\S+/;
      if (!re.test(newEmail)) {
        this.error = "Please enter a valid email address.";
        return;
      }

      if ((this.profile.email || "").toLowerCase() === newEmail) {
        this.message = "That’s already your current email.";
        return;
      }

      this.savingEmail = true;
      try {
        await axios.post(
          `${process.env.VUE_APP_API_URL}/api/request-email-change`,
          { email: newEmail },
          { headers: this.apiHeaders() }
        );

        this.pendingEmail = newEmail;
        this.emailForm.newEmail = "";
        this.message = `Verification email sent to ${newEmail}. Click the link to complete the change.`;
      } catch (e) {
        this.error = e.response?.data?.error || e.response?.data?.message || "Failed to send verification email.";
      } finally {
        this.savingEmail = false;
      }
    },
    async resendEmailChange() {
      this.clearAlerts();
      const email = (this.pendingEmail || "").trim().toLowerCase();
      if (!email) return;

      this.savingEmail = true;
      try {
        await axios.post(
          `${process.env.VUE_APP_API_URL}/api/request-email-change`,
          { email },
          { headers: this.apiHeaders() }
        );
        this.message = `Verification email resent to ${email}.`;
      } catch (e) {
        this.error = e.response?.data?.error || e.response?.data?.message || "Failed to resend verification email.";
      } finally {
        this.savingEmail = false;
      }
    },
    cancelPendingEmail() {
      this.pendingEmail = "";
      this.message = "Pending email change cancelled.";
    },
    toggleCurrentPasswordVisibility() {
      this.showCurrentPassword = !this.showCurrentPassword;
    },
    toggleNewPasswordVisibility() {
      this.showNewPassword = !this.showNewPassword;
    },
    toggleConfirmPasswordVisibility() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },
    async updatePassword() {
      this.clearAlerts();

      if (!this.pwForm.currentPassword) {
        this.error = "Please enter your current password.";
        return;
      }

      if (!this.passwordMeetsRules) {
        this.error = "Your new password doesn’t meet the requirements yet.";
        return;
      }

      if (!this.passwordMatches) {
        this.error = "New password and confirmation do not match.";
        return;
      }

      this.savingPassword = true;
      try {
        await axios.put(
          `${process.env.VUE_APP_API_URL}/api/profile/password`,
          {
            currentPassword: this.pwForm.currentPassword,
            newPassword: this.pwForm.newPassword
          },
          { headers: this.apiHeaders() }
        );

        // reset fields
        this.pwForm.currentPassword = "";
        this.pwForm.newPassword = "";
        this.pwForm.confirmPassword = "";

        // reset visibility
        this.showCurrentPassword = false;
        this.showNewPassword = false;
        this.showConfirmPassword = false;

        this.message = "Password updated successfully.";
      } catch (e) {
        this.error = e.response?.data?.error || "Failed to update password.";
      } finally {
        this.savingPassword = false;
      }
    }
  }
};
</script>
